import {
  AppLayout,
  AppLayoutProps,
  Flashbar,
  HelpPanel,
  TopNavigation,
} from "@amzn/awsui-components-console";
import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import mambaLogo from "@/assets/logo/mamba-logo.png";
import { useCart } from "@/contexts/CartContext";
import { useChat } from "@/contexts/ChatContext";
import { useClipPreview } from "@/contexts/ClipPreviewContext";
import { useNotifications } from "@/contexts/NotificationsContext";
import { usePanelContent } from "@/contexts/PanelContentContext";
import { useUser } from "@/contexts/UserContext";
import {
  CLIP_PREVIEW_DRAWER_ID,
  HELP_DRAWER_ID,
  PROCESSING_STEPS_DRAWER_ID,
  STAGE,
} from "@/utils/constants";

import ClipPreviewPanel from "../iconik/ClipPreviewPanel";
import { getRouteContent } from "../panels/routeContent";
import Sidebar from "./Sidebar";

export interface EnhancedLayoutProps {
  children: React.ReactNode;
  signOut?: () => void;
}

const EnhancedLayout: React.FC<EnhancedLayoutProps> = ({
  children,
  signOut,
}) => {
  const navigate = useNavigate();
  const {
    user: {
      attributes: { email, name },
      isAdmin,
    },
  } = useUser();

  const [navigationOpen, setNavigationOpen] = React.useState(false);

  const { items: cartItems } = useCart();
  // Active chat (empty string = a brand-new chat). Lets the Cart toggle send
  // the user back to where they came from, since the "Go back to Chat" button
  // is hidden once the cart has clips.
  const { conversationId } = useChat();

  const location = useLocation();
  const { panelContent, activeDrawerId, setActiveDrawerId } = usePanelContent();
  const { openLatest } = useClipPreview();

  // Help, Processing Steps, and Clip Preview are all right-rail drawers. Only
  // one drawer is open at a time, so Processing Steps and Clip Preview are
  // mutually exclusive and every toggle stays visible.
  const routeContent = useMemo(() => {
    const baseContent = getRouteContent(location.pathname);
    return {
      helpPanel: panelContent.helpPanel || baseContent.helpPanel,
      splitPanel: panelContent.splitPanel || baseContent.splitPanel,
    };
  }, [location.pathname, panelContent]);

  const isChatRoute = location.pathname === "/";
  const isCartRoute = location.pathname === "/cart";
  // The Clip Preview drawer is available on both the chat and the cart pages.
  const showClipPreview = isChatRoute || isCartRoute;

  const drawers = useMemo<AppLayoutProps.Drawer[]>(() => {
    const result: AppLayoutProps.Drawer[] = [];
    if (routeContent.helpPanel) {
      result.push({
        id: HELP_DRAWER_ID,
        ariaLabels: {
          drawerName: routeContent.helpPanel.header,
          closeButton: "Close help",
          triggerButton: "Toggle help",
          resizeHandle: "Resize help",
        },
        trigger: { iconName: "status-info" },
        content: (
          <HelpPanel header={<h2>{routeContent.helpPanel.header}</h2>}>
            {routeContent.helpPanel.content}
          </HelpPanel>
        ),
      });
    }
    if (isChatRoute && routeContent.splitPanel) {
      result.push({
        id: PROCESSING_STEPS_DRAWER_ID,
        ariaLabels: {
          drawerName: routeContent.splitPanel.header,
          closeButton: "Close processing steps",
          triggerButton: "Toggle processing steps",
          resizeHandle: "Resize processing steps",
        },
        trigger: { iconName: "transcript" },
        resizable: true,
        defaultSize: 400,
        content: routeContent.splitPanel.content,
      });
    }
    if (showClipPreview) {
      result.push({
        id: CLIP_PREVIEW_DRAWER_ID,
        ariaLabels: {
          drawerName: "Clip Preview",
          closeButton: "Close clip preview",
          triggerButton: "Toggle clip preview",
          resizeHandle: "Resize clip preview",
        },
        trigger: { iconName: "video-camera-on" },
        resizable: true,
        defaultSize: 360,
        content: <ClipPreviewPanel />,
      });
    }
    return result;
  }, [routeContent.helpPanel, routeContent.splitPanel, isChatRoute, showClipPreview]);

  const onDrawerChange = ({
    detail,
  }: {
    detail: AppLayoutProps.DrawerChangeDetail;
  }) => {
    const nextId = detail.activeDrawerId;
    setActiveDrawerId(nextId);
    if (nextId === CLIP_PREVIEW_DRAWER_ID) {
      // When opened from its own trigger (no row clicked), show the latest
      // accordion's clips.
      openLatest();
    }
  };

  const { items: notificationItems } = useNotifications();

  return (
    <div data-testid="layout">
      <TopNavigation
        identity={{
          href: "/",
          title: `Prime Video Live Sports: MAMBA${
            STAGE === "prod" ? "" : ` (${STAGE.toUpperCase()})`
          }`,
          logo: {
            src: mambaLogo,
            alt: "MAMBA Logo",
          },
        }}
        utilities={[
          {
            type: "button",
            // On the cart the button leaves it (returns to chat), so label it
            // accordingly; the opened-folder icon stays as the cart indicator.
            text: isCartRoute ? "Leave Cart" : "Cart",
            ariaLabel: isCartRoute ? "Leave Cart" : "Cart",
            iconName: isCartRoute ? "folder-open" : "folder",
            badge: cartItems.length > 0,
            disableUtilityCollapse: false,
            // Toggle: from anywhere else go to the cart; when already on the
            // cart go back to the originating chat (or a new chat), because the
            // in-page "Go back to Chat" button is unavailable once clips exist.
            onClick: () =>
              navigate(
                location.pathname === "/cart"
                  ? conversationId
                    ? `/?thread=${conversationId}`
                    : "/"
                  : "/cart"
              ),
          },
          {
            type: "menu-dropdown",
            text: name,
            description: `${email}${isAdmin ? " – ADMIN" : ""}`,
            iconName: "user-profile",
            items: [
              {
                id: "signout",
                text: "Sign out",
              },
            ],
            onItemClick: ({ detail }) => {
              if (detail.id === "signout" && signOut) {
                signOut();
              }
            },
          },
        ]}
      />
      <AppLayout
        headerSelector="#top-navigation"
        navigation={<Sidebar />}
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        drawers={drawers}
        activeDrawerId={activeDrawerId}
        onDrawerChange={onDrawerChange}
        notifications={<Flashbar items={notificationItems} stackItems />}
        content={children}
      />
    </div>
  );
};

export default EnhancedLayout;
